import React, { useState, useEffect, useContext } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import useApi from '../../hooks/useApi';
import haxios from '../../lib/haxios';
import { format, subDays } from 'date-fns';
import { UsersIcon, ChevronDownIcon, ChevronUpIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import BranchDropdown from '../../components/BranchDropdown';
import { exportToExcel } from '../../components/AdvancedReports/shared/exportUtils';

const formatNumber = (number) => {
  if (number === null || number === undefined || isNaN(number)) return '0.00';
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);
};

function ZeroSpendCard() {
  const { uinfo } = useContext(GlobalContext);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [isExpanded, setIsExpanded] = useState(false);
  const [zeroSpendCustomers, setZeroSpendCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get yesterday's date
  const yesterday = subDays(new Date(), 1);
  const yesterdayStr = format(yesterday, 'yyyy-MM-dd');

  // Fetch customer sales data
  const { data: customerSalesData, isLoading: salesLoading } = useApi({
    url: '/visiondashboard/rankCustomers',
    initial: []
  });

  // Fetch zero spend analysis
  useEffect(() => {
    const fetchZeroSpend = async () => {
      if (!selectedBranch || selectedBranch === 'all') {
        // If no branch selected, filter from customerSalesData
        if (customerSalesData && Array.isArray(customerSalesData)) {
          const zeroSpend = customerSalesData
            .filter(customer => {
              const yesterdaySales = customer.totalSalesYesterday || 0;
              return parseFloat(yesterdaySales) === 0;
            })
            .map(customer => ({
              account: customer._id,
              garageName: customer.GarageName,
              branch: customer.branch || 'Unknown',
              lastPurchaseDate: customer.lastOrderDate,
              sales30d: customer.totalSalesLast30Days || 0,
              avg30d: customer.totalSalesLast30Days ? customer.totalSalesLast30Days / 30 : 0,
              rep: customer.Rep || customer.rep || 'Unknown'
            }))
            .sort((a, b) => b.sales30d - a.sales30d);
          
          setZeroSpendCustomers(zeroSpend);
        }
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await haxios.get(
          `/visiondashboard/zero-spend/analysis/${encodeURIComponent(selectedBranch)}/${yesterdayStr}`
        );
        
        if (response.data && response.data.customers) {
          const customers = response.data.customers.map(customer => ({
            account: customer.account || customer._id,
            garageName: customer.garageName || customer.GarageName || customer.name,
            branch: selectedBranch,
            lastPurchaseDate: customer.lastOrderDate || customer.lastPurchaseDate,
            sales30d: customer.sales30d || customer.totalSalesLast30Days || 0,
            avg30d: customer.avg30d || (customer.totalSalesLast30Days ? customer.totalSalesLast30Days / 30 : 0),
            rep: customer.rep || customer.Rep || 'Unknown'
          }));
          setZeroSpendCustomers(customers);
        } else {
          setZeroSpendCustomers([]);
        }
      } catch (error) {
        console.error('Error fetching zero spend analysis:', error);
        setZeroSpendCustomers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchZeroSpend();
  }, [selectedBranch, customerSalesData, yesterdayStr]);

  const handleExport = () => {
    const exportData = zeroSpendCustomers.map(customer => ({
      'Account': customer.account,
      'Garage Name': customer.garageName,
      'Branch': customer.branch,
      'Last Purchase Date': customer.lastPurchaseDate 
        ? format(new Date(customer.lastPurchaseDate), 'dd/MM/yyyy')
        : 'N/A',
      '30d Sales': customer.sales30d,
      '30d Average': customer.avg30d,
      'Rep': customer.rep
    }));

    // Create columns for export
    const columns = [
      { key: 'Account', label: 'Account' },
      { key: 'Garage Name', label: 'Garage Name' },
      { key: 'Branch', label: 'Branch' },
      { key: 'Last Purchase Date', label: 'Last Purchase Date' },
      { key: '30d Sales', label: '30d Sales', format: 'currency' },
      { key: '30d Average', label: '30d Average', format: 'currency' },
      { key: 'Rep', label: 'Rep' }
    ];

    exportToExcel(exportData, {
      title: 'Zero Spend Customers',
      filename: `zero-spend-customers-${yesterdayStr}.xlsx`,
      columns,
      tableData: exportData
    });
  };

  const isLoading = loading || salesLoading;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Card Header */}
      <div 
        className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <UsersIcon className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Customers Who Didn't Purchase Yesterday</h3>
              <p className="text-sm text-gray-600 mt-1">
                {isLoading ? 'Loading...' : `${zeroSpendCustomers.length} customer${zeroSpendCustomers.length !== 1 ? 's' : ''} with zero spend on ${format(yesterday, 'dd/MM/yyyy')}`}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="text-2xl font-bold text-red-600">
                {isLoading ? '...' : zeroSpendCustomers.length}
              </div>
              <div className="text-xs text-gray-500">Customers</div>
            </div>
            {isExpanded ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          {/* Branch Filter and Export */}
          <div className="mb-4 flex items-center justify-between">
            <BranchDropdown
              branch={selectedBranch}
              setBranch={setSelectedBranch}
              placeholder="Filter by branch"
              clearable
            />
            {zeroSpendCustomers.length > 0 && (
              <button
                onClick={handleExport}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                <span>Export to Excel</span>
              </button>
            )}
          </div>

          {/* Customers Table */}
          {isLoading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading zero spend customers...</p>
            </div>
          ) : zeroSpendCustomers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <UsersIcon className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p>All customers made purchases yesterday!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Garage Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Branch
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Purchase
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      30d Sales
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      30d Average
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rep
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {zeroSpendCustomers.map((customer, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
                        {customer.account}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {customer.garageName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {customer.branch}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {customer.lastPurchaseDate 
                          ? format(new Date(customer.lastPurchaseDate), 'dd/MM/yyyy')
                          : 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                        £{formatNumber(customer.sales30d)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500">
                        £{formatNumber(customer.avg30d)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {customer.rep}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ZeroSpendCard;

